import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { apiFetch } from '../../utils/api'
import styles from './EmployeesPage.module.css'

interface EmployeeSummary {
  id: string
  firstName: string
  lastName: string
  name: string
  email: string | null
  department: string | null
  position: string | null
  entryDate: string
  exitDate: string | null
  isActive: boolean
}

export function EmployeesPage() {
  const { t } = useTranslation()
  const [employees, setEmployees] = useState<EmployeeSummary[]>([])
  const [search, setSearch] = useState('')
  const [showInactive, setShowInactive] = useState(false)
  const [loading, setLoading] = useState(true)

  const loadEmployees = useCallback(async () => {
    setLoading(true)
    const params = new URLSearchParams()
    if (search) params.set('search', search)
    if (showInactive) params.set('active', 'false')

    const res = await apiFetch<{ employees: EmployeeSummary[] }>(
      `/employees?${params}`,
    )
    if (res.data) setEmployees(res.data.employees)
    setLoading(false)
  }, [search, showInactive])

  useEffect(() => { loadEmployees() }, [loadEmployees])

  const formatDate = (d: string | null): string => {
    if (!d) return '-'
    return new Date(d).toLocaleDateString()
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1>{t('employees.title')}</h1>
          <p className={styles.subtitle}>
            {employees.length} {t('employees.count')}
          </p>
        </div>
        <Link to="/employees/new" className={styles.addBtn}>
          {t('employees.add')}
        </Link>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <input
          type="search"
          className={styles.searchInput}
          placeholder={t('common.search')}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <label className={styles.checkLabel}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={e => setShowInactive(e.target.checked)}
          />
          {t('employees.show_inactive')}
        </label>
      </div>

      {/* List */}
      {loading ? (
        <p className={styles.loading}>{t('common.loading')}</p>
      ) : employees.length === 0 ? (
        <div className={styles.empty}>
          <p>{t('employees.empty')}</p>
        </div>
      ) : (
        <div className={styles.list}>
          {employees.map(emp => (
            <Link key={emp.id} to={`/employees/${emp.id}`} className={styles.card}>
              <div className={styles.cardMain}>
                <span className={styles.cardName}>{emp.lastName} {emp.firstName}</span>
                <span className={styles.cardPosition}>{emp.position ?? '-'}</span>
              </div>
              <div className={styles.cardMeta}>
                {emp.department && <span className={styles.cardDept}>{emp.department}</span>}
                <span className={styles.cardDate}>{formatDate(emp.entryDate)}</span>
                {!emp.isActive && <span className={styles.cardInactive}>{t('employees.inactive')}</span>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
